import {
  applyLanguage,
  recallLanguage,
  resolveDinerLanguage,
  toSupportedLocale,
  type SupportedLocale,
} from "@repo/i18n";

/**
 * Widget-side diner language resolution (localization-i18n R1/R7), composed from `@repo/i18n`'s
 * locked primitives so the widget can never import i18next directly (dependency-cruiser enforces it).
 *
 * The explicit choice is the `?lang=` deep-link param if valid, else this session's remembered pick
 * (`sessionStorage`, set by `applyLanguage`). Precedence then follows `resolveDinerLanguage`:
 * explicit → first supported browser language → venue default → "fr".
 */
export function explicitChoice(langParam: string | null | undefined): string | null {
  return toSupportedLocale(langParam) ?? recallLanguage() ?? null;
}

/**
 * The language to use BEFORE the venue config is known (first paint): resolve against the platform
 * default "fr" as the venue default. Re-run with the real default once config lands.
 */
export function resolveInitialLanguage(
  langParam: string | null | undefined,
  browserLanguages: readonly string[],
) {
  return resolveDinerLanguage(explicitChoice(langParam), browserLanguages, "fr");
}

/**
 * Resolve against the venue's real default once config is loaded (P5 / ADR-0013 — this is a
 * FRENCH-FIRST product, and a French venue must not silently render English just because the diner's
 * browser is English). Precedence, highest first:
 *   1. `explicit` — the diner's in-session choice (the `?lang=` param / remembered session pick);
 *   2. the venue's own `default_language` — the venue's editorial choice wins over the device;
 *   3. `browserLanguages` — only a TIEBREAKER, used when the venue default is somehow unsupported;
 *   4. `"fr"` — the platform floor.
 * This deliberately DIVERGES from the pre-config `resolveInitialLanguage` (which has no venue default
 * yet and follows the R1 browser-first chain for first paint); once config lands, the venue default
 * is authoritative for any diner who never picked a language.
 */
export function resolveVenueLanguage(
  langParam: string | null | undefined,
  browserLanguages: readonly string[],
  venueDefault: string,
): SupportedLocale {
  const fromExplicit = toSupportedLocale(explicitChoice(langParam));
  if (fromExplicit) return fromExplicit;
  const fromVenue = toSupportedLocale(venueDefault);
  if (fromVenue) return fromVenue;
  for (const tag of browserLanguages) {
    const supported = toSupportedLocale(tag);
    if (supported) return supported;
  }
  return "fr";
}

/** Apply the initial language in place (switch i18next + stamp <html lang> + remember the choice). */
export async function bootstrapLanguage(
  langParam: string | null | undefined,
  browserLanguages: readonly string[],
): Promise<void> {
  await applyLanguage(resolveInitialLanguage(langParam, browserLanguages));
}
